import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Database, X, ChevronDown, Loader, Filter, Star, ExternalLink, Zap, Flame, Droplets, Settings, Award, TrendingUp, Wind } from 'lucide-react';
import { debounce } from '../utils/helpers';
import { supabase } from '../lib/supabaseClient';

export interface PCDBHeatingSystem {
  id: string;
  brand: string;
  model: string;
  model_qualifier?: string;
  type: string;
  fuel?: string;
  main_type?: string;
  output_power_kw?: number;
  sap_winter_seasonal_efficiency?: number;
  sap_summer_seasonal_efficiency?: number;
  efficiency_category?: string;
  condensing?: boolean;
  flue_type?: string;
  fan_assisted?: boolean;
  year_of_commission?: string;
  features?: string[];
  certifications?: string[];
  pcdb_reference?: string;
  boiler_id?: string;
  last_updated?: string;
  is_discontinued?: boolean;
  replacement_models?: string[];
  // Legacy fields for backward compatibility
  kw_output?: number;
  fuel_type?: string;
  efficiency_rating?: string;
  installation_type?: string;
}

interface PCDBSearchProps {
  onSelect: (system: PCDBHeatingSystem) => void;
  selectedSystem?: PCDBHeatingSystem | null;
  placeholder?: string;
  className?: string;
  onManualEntryChange?: (isManual: boolean) => void;
  isManualEntry?: boolean;
}

interface SearchFilters {
  type: string;
  brand: string;
  minOutput: string;
  maxOutput: string;
  fuelType: string;
  installationType: string;
  showDiscontinued: boolean;
}

const PCDBSearch: React.FC<PCDBSearchProps> = ({ 
  onSelect, 
  selectedSystem, 
  placeholder = "Search by brand, model, GC number, or PCDB reference (e.g., Worcester Bosch, 47-044-30, 4704430)...",
  className = "",
  onManualEntryChange,
  isManualEntry = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<PCDBHeatingSystem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [pcdbAvailable, setPcdbAvailable] = useState(true);
  const [filters, setFilters] = useState<SearchFilters>({
    type: '',
    brand: '',
    minOutput: '',
    maxOutput: '',
    fuelType: '',
    installationType: '',
    showDiscontinued: false
  });
  
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize search term when selectedSystem changes
  useEffect(() => {
    if (selectedSystem) {
      setSearchTerm(`${selectedSystem.brand} ${selectedSystem.model}`);
    } else {
      setSearchTerm('');
    }
  }, [selectedSystem]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (term: string, currentFilters: SearchFilters) => {
      // Don't search if in manual entry mode
      if (isManualEntry) {
        setSearchResults([]);
        setIsLoading(false);
        return;
      }

      // Don't search if no meaningful input - Allow single character searches and filter-only searches
      if (term.length < 1 && Object.values(currentFilters).every(v => !v || v === false)) {
        setSearchResults([]);
        setIsLoading(false);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      try {
        const results = await searchPCDBHeatingSystems(term, currentFilters);
        const hasFilters = Object.values(currentFilters).some(v => v && v !== false);
        setSearchResults(results);
        setIsOpen(results.length > 0 || term.length >= 1 || hasFilters);
        
        // Update PCDB availability status based on results
        if (results.length === 0 && (term.length >= 1 || hasFilters)) {
          setPcdbAvailable(false);
        } else {
          setPcdbAvailable(true);
        }
      } catch (error) {
        console.error('PCDB search error:', error);
        setSearchResults([]);
        setPcdbAvailable(false);
        setIsOpen(false);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    [isManualEntry]
  );

  // Trigger search when term or filters change (but not in manual mode or when a system is selected)
  useEffect(() => {
    if (!isManualEntry && !selectedSystem) {
      debouncedSearch(searchTerm, filters);
    } else {
      // Clear search results when in manual mode or when a system is selected
      setSearchResults([]);
      setIsOpen(false);
    }
  }, [searchTerm, filters, debouncedSearch, isManualEntry, selectedSystem]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    
    // If user is typing and we have a selected system, clear the selection to allow new search
    if (selectedSystem && newValue !== `${selectedSystem.brand} ${selectedSystem.model}`) {
      onSelect(null as any);
    }
  };

  const handleSystemSelect = (system: PCDBHeatingSystem) => {
    onSelect(system);
    setSearchTerm(`${system.brand} ${system.model}`);
    setIsOpen(false);
    setShowFilters(false);
  };

  const handleFilterChange = (key: keyof SearchFilters, value: string | boolean) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      brand: '',
      minOutput: '',
      maxOutput: '',
      fuelType: '',
      installationType: '',
      showDiscontinued: false
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v && v !== false);

  return (
    <div ref={searchRef} className={`space-y-4 relative ${className}`}>
      {/* Manual Entry Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-3">
          <Database className="h-5 w-5 text-gray-600" />
          <div>
            <h4 className="font-medium text-gray-900">Heating System Selection</h4>
            <p className="text-sm text-gray-600">
              Choose from PCDB database or enter manually
              {!pcdbAvailable && (
                <span className="text-orange-600 ml-1">
                  (PCDB data not available - use manual entry)
                </span>
              )}
            </p>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isManualEntry}
            onChange={(e) => onManualEntryChange?.(e.target.checked)}
            className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
          />
          <span className="text-sm font-medium text-gray-700">Enter details manually</span>
        </label>
      </div>

      {/* PCDB Search Section - Only show when not manual entry */}
      {!isManualEntry && (
        <div className="space-y-4 relative">
          {/* Enhanced Search Input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={() => {
                if (searchResults.length > 0 && !selectedSystem) setIsOpen(true);
              }}
              className="w-full pl-12 pr-16 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 bg-white placeholder-gray-500 text-gray-900 transition-all duration-200 hover:border-gray-300"
              placeholder="Search by brand, model, GC number, or PCDB reference (e.g., Worcester Bosch, 47-044-30, 4704430)..."
            />
            <div className="absolute inset-y-0 right-0 pr-4 flex items-center space-x-2">
              {isLoading && (
                <div className="flex items-center gap-1">
                  <Loader className="h-4 w-4 animate-spin text-yellow-500" />
                  <span className="text-xs text-gray-500 hidden sm:inline">Searching...</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  hasActiveFilters 
                    ? 'text-yellow-600 bg-yellow-100 hover:bg-yellow-200' 
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
                title={hasActiveFilters ? `${Object.values(filters).filter(v => v && v !== false).length} filters active` : 'Open filters'}
              >
                <Filter className="h-4 w-4" />
                {hasActiveFilters && (
                  <span className="absolute -top-1 -right-1 h-3 w-3 bg-yellow-500 rounded-full text-xs flex items-center justify-center text-white font-bold">
                    {Object.values(filters).filter(v => v && v !== false).length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Search Examples - Only show when not searching */}
          {!isOpen && (
            <div className="text-xs text-gray-500 space-y-1">
              <p className="font-medium">💡 Search examples:</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-gray-100 rounded text-gray-600">Worcester Bosch</span>
                <span className="px-2 py-1 bg-gray-100 rounded text-gray-600">47-044-30</span>
                <span className="px-2 py-1 bg-gray-100 rounded text-gray-600">4704430</span>
                <span className="px-2 py-1 bg-gray-100 rounded text-gray-600">Greenstar 30i</span>
                <span className="px-2 py-1 bg-gray-100 rounded text-gray-600">Vaillant</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                💡 Tip: GC numbers work with or without dashes (47-044-30 = 4704430). Use filters to browse by type.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Enhanced Selected System Info Card - Only show when PCDB system is selected and not manual entry */}
      {selectedSystem && !isOpen && !isManualEntry && (
        <div className="p-4 sm:p-6 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                {selectedSystem.type === 'gas-boiler' && <Flame className="h-6 w-6 text-orange-500" />}
                {selectedSystem.type === 'oil-boiler' && <Droplets className="h-6 w-6 text-blue-500" />}
                {selectedSystem.type === 'heat-pump' && <Zap className="h-6 w-6 text-green-500" />}
                {selectedSystem.type === 'electric-heating' && <Zap className="h-6 w-6 text-yellow-500" />}
                {!['gas-boiler', 'oil-boiler', 'heat-pump', 'electric-heating'].includes(selectedSystem.type) && 
                  <Settings className="h-6 w-6 text-gray-500" />}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-lg text-gray-900">
                    {selectedSystem.brand} {selectedSystem.model}
                  </h3>
                  <Award className="h-5 w-5 text-green-600" />
                </div>
                {selectedSystem.model_qualifier && (
                  <p className="text-sm text-gray-600 mb-1">
                    Model Qualifier: {selectedSystem.model_qualifier}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full font-medium capitalize">
                    {selectedSystem.type.replace('-', ' ')}
                  </span>
                  {selectedSystem.is_discontinued && (
                    <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full font-medium">
                      Discontinued
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                onSelect(null as any);
                setSearchTerm('');
              }}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
              title="Clear selection"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Technical Specifications Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {/* Power Output */}
            {(selectedSystem.output_power_kw || selectedSystem.kw_output) && (
              <div className="bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="h-4 w-4 text-orange-500" />
                  <span className="text-sm font-medium text-gray-700">Power Output</span>
                </div>
                <p className="text-lg font-bold text-gray-900">
                  {selectedSystem.output_power_kw || selectedSystem.kw_output}kW
                </p>
              </div>
            )}

            {/* Winter Efficiency */}
            {selectedSystem.sap_winter_seasonal_efficiency && (
              <div className="bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <Award className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium text-gray-700">Winter Efficiency</span>
                </div>
                <p className="text-lg font-bold text-green-600">
                  {selectedSystem.sap_winter_seasonal_efficiency}%
                </p>
              </div>
            )}

            {/* Summer Efficiency */}
            {selectedSystem.sap_summer_seasonal_efficiency && (
              <div className="bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <Award className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm font-medium text-gray-700">Summer Efficiency</span>
                </div>
                <p className="text-lg font-bold text-yellow-600">
                  {selectedSystem.sap_summer_seasonal_efficiency}%
                </p>
              </div>
            )}

            {/* Fuel Type */}
            {(selectedSystem.fuel || selectedSystem.fuel_type) && (
              <div className="bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <Droplets className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium text-gray-700">Fuel Type</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 capitalize">
                  {selectedSystem.fuel || selectedSystem.fuel_type}
                </p>
              </div>
            )}

            {/* Installation Type */}
            {(selectedSystem.main_type || selectedSystem.installation_type) && (
              <div className="bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <Settings className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-medium text-gray-700">Installation</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 capitalize">
                  {selectedSystem.main_type || selectedSystem.installation_type}
                </p>
              </div>
            )}

            {/* Flue Type */}
            {selectedSystem.flue_type && (
              <div className="bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  <Wind className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Flue Type</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 capitalize">
                  {selectedSystem.flue_type}
                </p>
              </div>
            )}
          </div>

          {/* Features and Certifications */}
          <div className="space-y-3">
            {/* Features */}
            {selectedSystem.features && selectedSystem.features.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-500" />
                  Features
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedSystem.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full font-medium"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Certifications */}
            {selectedSystem.certifications && selectedSystem.certifications.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Award className="h-4 w-4 text-blue-500" />
                  Certifications
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedSystem.certifications.map((cert, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded-full font-medium"
                    >
                      {cert}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Properties */}
            <div className="flex flex-wrap gap-3 text-sm">
              {selectedSystem.condensing && (
                <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full">
                  <Droplets className="h-3 w-3" />
                  Condensing
                </span>
              )}
              {selectedSystem.fan_assisted && (
                <span className="flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full">
                  <Wind className="h-3 w-3" />
                  Fan Assisted
                </span>
              )}
              {selectedSystem.pcdb_reference && (
                <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
                  <Database className="h-3 w-3" />
                  PCDB: {selectedSystem.pcdb_reference}
                </span>
              )}
              {selectedSystem.boiler_id && (
                <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
                  <ExternalLink className="h-3 w-3" />
                  ID: {selectedSystem.boiler_id}
                </span>
              )}
              <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
                <Database className="h-3 w-3" />
                Official PCDB
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Filters Panel - Only show when not manual entry */}
      {showFilters && !isManualEntry && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-gray-600" />
              <h4 className="font-semibold text-gray-900">Search Filters</h4>
            </div>
            <button
              onClick={clearFilters}
              className="text-sm text-yellow-600 hover:text-yellow-700 font-medium transition-colors"
            >
              Clear All
            </button>
          </div>
          
          <div className="space-y-4">
            {/* System Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Flame className="inline h-4 w-4 mr-1" />
                System Type
              </label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              >
                <option value="">All Types</option>
                <option value="gas-boiler">🔥 Gas Boiler</option>
                <option value="oil-boiler">🛢️ Oil Boiler</option>
                <option value="heat-pump">⚡ Heat Pump</option>
                <option value="electric-heating">🔌 Electric Heating</option>
                <option value="biomass">🌿 Biomass</option>
                <option value="hybrid">🔄 Hybrid System</option>
                <option value="storage-heater">🔋 Storage Heater</option>
                <option value="micro-chp">⚙️ Micro CHP</option>
              </select>
            </div>

            {/* Brand and Power Output */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Award className="inline h-4 w-4 mr-1" />
                  Brand
                </label>
                <input
                  type="text"
                  placeholder="e.g. Dimplex, Elnur, Worcester Bosch"
                  value={filters.brand}
                  onChange={(e) => handleFilterChange('brand', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Zap className="inline h-4 w-4 mr-1" />
                  Min Output (kW)
                </label>
                <input
                  type="number"
                  placeholder="0"
                  value={filters.minOutput}
                  onChange={(e) => handleFilterChange('minOutput', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <TrendingUp className="inline h-4 w-4 mr-1" />
                  Max Output (kW)
                </label>
                <input
                  type="number"
                  placeholder="100"
                  value={filters.maxOutput}
                  onChange={(e) => handleFilterChange('maxOutput', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Options */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={filters.showDiscontinued}
                  onChange={(e) => handleFilterChange('showDiscontinued', e.target.checked)}
                  className="mr-2 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                />
                Include discontinued models
              </label>
              
              {hasActiveFilters && (
                <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {Object.values(filters).filter(v => v && v !== false).length} filter(s) active
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Search Results - Only show when not manual entry */}
      {isOpen && searchResults.length > 0 && !isManualEntry && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-80 overflow-y-auto">
          <div className="p-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 font-medium">
                {searchResults.length} heating system{searchResults.length !== 1 ? 's' : ''} found
              </span>
              <span className="text-gray-500">Click to select</span>
            </div>
          </div>
          
          {searchResults.map((system, index) => {
            const getSystemIcon = (type: string) => {
              switch (type) {
                case 'gas-boiler': return <Flame className="h-4 w-4 text-orange-500" />;
                case 'oil-boiler': return <Droplets className="h-4 w-4 text-blue-500" />;
                case 'heat-pump': return <Zap className="h-4 w-4 text-green-500" />;
                case 'electric-heating': return <Zap className="h-4 w-4 text-yellow-500" />;
                default: return <Settings className="h-4 w-4 text-gray-500" />;
              }
            };

            const getEfficiencyBadge = (efficiency: number | undefined) => {
              if (!efficiency) return null;
              let color = 'bg-gray-100 text-gray-700';
              if (efficiency >= 90) color = 'bg-green-100 text-green-700';
              else if (efficiency >= 80) color = 'bg-yellow-100 text-yellow-700';
              else if (efficiency >= 70) color = 'bg-orange-100 text-orange-700';
              
              return (
                <span className={`px-2 py-1 text-xs rounded-full ${color} font-medium`}>
                  {efficiency}% efficient
                </span>
              );
            };

            return (
              <div
                key={system.id}
                onClick={() => handleSystemSelect(system)}
                className="p-3 sm:p-4 hover:bg-yellow-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    {getSystemIcon(system.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900 truncate text-sm sm:text-base">
                            {system.brand} {system.model}
                            {system.model_qualifier && (
                              <span className="font-medium text-gray-700 ml-1">
                                {system.model_qualifier}
                              </span>
                            )}
                          </h4>
                          {system.is_discontinued && (
                            <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full font-medium">
                              Discontinued
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 sm:gap-3 mb-2 text-xs sm:text-sm text-gray-600 flex-wrap">
                          <span className="capitalize font-medium">
                            {system.type.replace('-', ' ')}
                          </span>
                          {(system.output_power_kw || system.kw_output) && (
                            <span className="flex items-center gap-1">
                              <TrendingUp className="h-3 w-3" />
                              {system.output_power_kw || system.kw_output}kW
                            </span>
                          )}
                          {(system.main_type || system.installation_type) && (
                            <span className="capitalize text-gray-500">
                              {system.main_type || system.installation_type}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 mb-2">
                          {getEfficiencyBadge(system.sap_winter_seasonal_efficiency)}
                          {system.condensing && (
                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                              Condensing
                            </span>
                          )}
                          {system.fan_assisted && (
                            <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
                              Fan Assisted
                            </span>
                          )}
                        </div>

                        {system.features && system.features.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {system.features.slice(0, 2).map((feature, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                              >
                                {feature}
                              </span>
                            ))}
                            {system.features.length > 2 && (
                              <span className="text-xs text-gray-500 px-1">
                                +{system.features.length - 2} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-shrink-0 ml-3 text-right">
                        {system.boiler_id && (
                          <div className="text-xs text-blue-600 font-medium mb-1">
                            GC: {system.boiler_id}
                          </div>
                        )}
                        {system.pcdb_reference && (
                          <div className="text-xs text-gray-400 mb-1">
                            PCDB: {system.pcdb_reference}
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Database className="h-3 w-3" />
                          Official PCDB
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Enhanced No Results - Only show when not manual entry */}
      {isOpen && !isLoading && searchResults.length === 0 && (searchTerm.length >= 1 || hasActiveFilters) && !isManualEntry && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] p-4 sm:p-6 text-center">
          <div className="flex flex-col items-center gap-3">
            <Search className="h-8 w-8 text-gray-300" />
            <div>
              <p className="text-gray-600 font-medium">No heating systems found</p>
              <p className="text-sm text-gray-500 mt-1">
                Try adjusting your search terms or filters, or check if PCDB data has been imported
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-yellow-600 hover:text-yellow-700 font-medium underline"
                >
                  Clear all filters
                </button>
              )}
              <div className="text-xs text-gray-400 space-y-1">
                <p>💡 Tip: Use manual entry if PCDB data is not available</p>
                <p>🔧 To set up PCDB: Run setup script in Supabase, then import data via Settings</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// API function to search PCDB heating systems
async function searchPCDBHeatingSystems(
  searchTerm: string, 
  filters: SearchFilters
): Promise<PCDBHeatingSystem[]> {
  try {

    // Skip the health check and go directly to the search query
    // If table doesn't exist, the main query will fail gracefully

    let query = supabase
      .from('pcdb_heating_systems')
      .select('*');

    // Text search across brand, model, model_qualifier, and boiler_id (GC number) for comprehensive coverage
    if (searchTerm.length >= 1) {
      // For GC number search, also search with dashes removed to handle formats like "47-044-30" vs "4704430"
      const normalizedSearchTerm = searchTerm.replace(/[-\s]/g, '');
      
      // Build basic search conditions
      const searchConditions = [
        `brand.ilike.%${searchTerm}%`,
        `model.ilike.%${searchTerm}%`,
        `model_qualifier.ilike.%${searchTerm}%`,
        `boiler_id.ilike.%${searchTerm}%`,
        `pcdb_reference.ilike.%${searchTerm}%`
      ];
      
      // If the search term contains only numbers/dashes, add flexible GC number search
      if (/^[\d\-\s]+$/.test(searchTerm) && normalizedSearchTerm !== searchTerm) {
        // Add search for the normalized version (without dashes/spaces)
        searchConditions.push(`boiler_id.ilike.%${normalizedSearchTerm}%`);
        searchConditions.push(`pcdb_reference.ilike.%${normalizedSearchTerm}%`);
        console.log(`🔍 GC Number Search: "${searchTerm}" also searching for "${normalizedSearchTerm}" for dash-flexible matching`);
      }
      
      // If the search term is all numbers (no dashes), also search for common dash patterns
      if (/^\d+$/.test(searchTerm) && searchTerm.length >= 6) {
        // For patterns like "4704430" -> also search for "47-044-30", "470-44-30", etc.
        const patterns = [];
        if (searchTerm.length === 7) {
          patterns.push(`${searchTerm.slice(0,2)}-${searchTerm.slice(2,5)}-${searchTerm.slice(5)}`); // XX-XXX-XX
          patterns.push(`${searchTerm.slice(0,3)}-${searchTerm.slice(3,5)}-${searchTerm.slice(5)}`); // XXX-XX-XX
        }
        if (searchTerm.length === 6) {
          patterns.push(`${searchTerm.slice(0,2)}-${searchTerm.slice(2,4)}-${searchTerm.slice(4)}`); // XX-XX-XX
        }
        
        patterns.forEach(pattern => {
          searchConditions.push(`boiler_id.ilike.%${pattern}%`);
          searchConditions.push(`pcdb_reference.ilike.%${pattern}%`);
        });
        
        if (patterns.length > 0) {
          console.log(`🔍 GC Number Search: "${searchTerm}" also searching for dash patterns: ${patterns.join(', ')}`);
        }
      }
      
      query = query.or(searchConditions.join(','));
    }

    // Apply filters
    if (filters.type) {
      query = query.eq('type', filters.type);
    }
    
    if (filters.brand) {
      query = query.ilike('brand', `%${filters.brand}%`);
    }

    if (filters.minOutput) {
      query = query.gte('output_power_kw', parseFloat(filters.minOutput));
    }

    if (filters.maxOutput) {
      query = query.lte('output_power_kw', parseFloat(filters.maxOutput));
    }

    if (filters.fuelType) {
      query = query.eq('fuel', filters.fuelType);
    }

    if (filters.installationType) {
      query = query.eq('main_type', filters.installationType);
    }

    if (!filters.showDiscontinued) {
      query = query.eq('is_discontinued', false);
    }

    // Execute the query - Remove limit to allow access to full database
    const { data, error } = await query
      .order('brand', { ascending: true })
      .order('model', { ascending: true });

    if (error) {
      // Check if it's a table not found error
      if (error.message?.includes('relation "pcdb_heating_systems" does not exist')) {
        console.warn('PCDB table does not exist yet. Please run the PCDB setup and import data first.');
        console.warn('To set up PCDB: 1) Run sql/setup_pcdb_table.sql in Supabase, 2) Import PCDB data via Settings');
        

        return [];
      }
      console.error('Supabase query error:', error);
      return [];
    }

    // Check if table is empty
    if (!data || data.length === 0) {
      console.warn('PCDB search returned no results. Table may be empty or no matching data found.');
      return [];
    }

    // Debug logging for successful queries
    if (data && data.length > 0) {
      console.log(`🔍 PCDB Search Results for "${searchTerm}":`, {
        totalResults: data.length,
        systemTypes: [...new Set(data.map(item => item.type))],
        brands: [...new Set(data.map(item => item.brand))],
        firstFewResults: data.slice(0, 3).map(item => ({
          brand: item.brand,
          model: item.model,
          type: item.type,
          fuel: item.fuel
        }))
      });
      
      // Special debugging for Dimplex searches
      if (searchTerm.toLowerCase().includes('dimplex')) {
        console.log('🔌 Dimplex-specific results:', data.map(item => ({
          brand: item.brand,
          model: item.model,
          type: item.type,
          fuel: item.fuel,
          main_type: item.main_type
        })));
      }
    }

    return data || [];
  } catch (error) {
    console.error('PCDB search error:', error);
    return [];
  }
}

export default PCDBSearch; 